import { MarkdownHint } from '../markdown-hint';
import { ListEmptyState, ListFilterStatus, useListFilters } from '../list-filter-feedback';
import { useUnsavedActions } from '../../general/unsaved-changes-context';
import '../../assets/entity-list.css';
import { useEffect, useState, useContext, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ListCourseDto } from "../../general/dtos";
import { GeneralErrorDialog } from '../dialogs/error-dialog';
import { CourseManagementDataContext, CourseManagementDataContextValue } from '../../providers/course-management-provider';
import { DeleteEntityConfirmDialog } from '../dialogs/delete-entity-confirm';
import { useContentPageNavigation } from '../content-page';

import { CourseFolderTree, COURSE_DRAG_TYPE } from './course-folder-tree';
import { useBookmarkSelection } from '../../general/bookmark-selection';

interface CourseListProps {
}

export function CourseList({}: CourseListProps) {
    const { requestAction } = useUnsavedActions();
    const { showDetails } = useContentPageNavigation();
    const ctx: CourseManagementDataContextValue | null = useContext<CourseManagementDataContextValue | null>(CourseManagementDataContext);
    const { mode, allCourses, allCoursesLoading, allCoursesError, selectedCourseError, selectedCourseKey,
        startCreateCourse, selectCourse, startEditCourse, deleteCourse, cancelEditing, loadAllCourses
     } = ctx!;

    const [folderBookmark, setFolderBookmark] = useBookmarkSelection('folder');
    const folderId = folderBookmark && /^[1-9][0-9]*$/.test(folderBookmark) && Number.isSafeInteger(Number(folderBookmark)) ? Number(folderBookmark) : null;
    const courses = allCourses.filter(course => (course.folder_id ?? null) === folderId);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const listRef = useRef<DataTable<ListCourseDto[]>>(null);
    const lastClickedKey = useRef<string | null>(null);
    const dragStartedOnControl = useRef(false);
    useEffect(() => { setSelectedKeys([]); lastClickedKey.current = null; }, [folderId]);
    useEffect(() => { setSelectedKeys(selectedCourseKey ? [selectedCourseKey] : []); }, [selectedCourseKey]);
    const visibleCourseKeys = () => Array.from(listRef.current?.getTable()?.querySelectorAll<HTMLElement>('[data-course-key]') ?? [])
        .map(element => element.dataset.courseKey!);
    const listFilters = useListFilters({ name: 'startsWith', created_at: 'startsWith', updated_at: 'startsWith' });
    const [selectedCourse, setSelectedCourse] = useState<ListCourseDto | null>(null);
    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [errorDetails, setErrorDetails] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    useEffect(() => {
        setSelectedCourse(allCourses.find(course => course.course_key === selectedCourseKey) ?? null);
    }, [allCourses, selectedCourseKey]);

    useEffect(() => {
        if (allCoursesError) {
            setError(allCoursesError);
            setErrorTitle('Error loading course list');
            setErrorDetails('There was an unhandled error while trying to load course list. Please try again later or contact support :)');
        }
        if (selectedCourseError) {
            setError(selectedCourseError);
            setErrorTitle(mode === 'view' ? 'Unable to load course' : 'Unable to save course');
            setErrorDetails(mode === 'view' ? 'Try opening the course again. If the problem continues, contact support.'
                : 'Your changes are still in the editor. Review the message, make any corrections, and save again.');
        }
    }, [allCoursesError, selectedCourseError, mode]);

    const nameBodyTemplate = (row: ListCourseDto) => {
        return (<MarkdownHint data-course-key={row.course_key} content={row.description}>{row.name}</MarkdownHint>);
    };

    return (<>
        <div className="entity-list-container">
            <CourseFolderTree folderId={folderId} selectedKeys={selectedKeys.filter(key => courses.some(course => course.course_key === key))}
                beforeChange={cancelEditing} onSelect={id => requestAction(() => {
                    cancelEditing(); selectCourse(null); setSelectedKeys([]);
                    setFolderBookmark(id === null ? null : String(id));
                })} onMoved={async () => { setSelectedKeys([]); await loadAllCourses(); }} />
            <div className="entity-list-toolbar admin-toolbar">
                <div className="selection-controls">
                    <Button label="All" text size="small" onClick={() => requestAction(() => setSelectedKeys(visibleCourseKeys()))}/>
                    <Button label="None" text size="small" onClick={() => setSelectedKeys([])}/>
                    <Button label="Invert" text size="small" onClick={() => requestAction(() => setSelectedKeys(visibleCourseKeys().filter(key => !selectedKeys.includes(key))))}/>
                    <Button label="New&nbsp;course..." icon="pi pi-plus" className="p-button-text p-button-sm" 
                        onClick={() => { startCreateCourse(); showDetails(); }}/>
                </div>
            </div>

            <ListFilterStatus count={listFilters.count} onClear={listFilters.clear} />
            <div className="entity-list-wrapper">
                <DataTable ref={listRef}
                    lazy={false}
                    filterDisplay={"menu"}
                    filters={listFilters.filters}
                    onFilter={event => listFilters.setFilters(event.filters)}
                    dataKey="course_key"
                    loading={allCoursesLoading}

                    selectionMode="multiple"
                    selection={courses.filter(course => selectedKeys.includes(course.course_key))}
                    onRowClick={event => {
                        if ((event.originalEvent.target as Element).closest('button, a, input, [role="button"]')) return;
                        requestAction(() => {
                            const row = event.data as ListCourseDto;
                            const key = row.course_key;
                            const original = event.originalEvent;
                            if (original.shiftKey && lastClickedKey.current) {
                                const keys = visibleCourseKeys();
                                const start = keys.indexOf(lastClickedKey.current), end = keys.indexOf(key);
                                if (start >= 0 && end >= 0) setSelectedKeys(previous => Array.from(new Set([...previous, ...keys.slice(Math.min(start,end), Math.max(start,end)+1)])));
                                else setSelectedKeys([key]);
                            } else if (original.ctrlKey || original.metaKey) {
                                setSelectedKeys(previous => previous.includes(key) ? previous.filter(item => item !== key) : [...previous,key]);
                            } else { setSelectedKeys([key]); setSelectedCourse(row); selectCourse(key); showDetails(); }
                            lastClickedKey.current = key;
                        });
                    }}
                    pt={{bodyRow:{
                        draggable: !allCoursesLoading, className:'course-draggable-row',
                        onMouseDownCapture:(event:React.MouseEvent<HTMLTableRowElement>)=>{
                            dragStartedOnControl.current=event.target instanceof Element && !!event.target.closest('button,a,input,textarea,select,[role="button"]');
                            if(!dragStartedOnControl.current && !allCoursesLoading){event.currentTarget.draggable=true;event.stopPropagation();}
                        },
                        onDragStartCapture:(event:React.DragEvent<HTMLTableRowElement>)=>{
                            event.stopPropagation();
                            const key=event.currentTarget.querySelector<HTMLElement>('[data-course-key]')?.dataset.courseKey;
                            if(!key || allCoursesLoading || dragStartedOnControl.current){event.preventDefault();return;}
                            const keys=selectedKeys.includes(key)?selectedKeys.filter(value=>courses.some(course=>course.course_key===value)):[key];
                            window.getSelection()?.removeAllRanges();event.dataTransfer.effectAllowed='move';
                            event.dataTransfer.setData(COURSE_DRAG_TYPE,JSON.stringify(keys));
                            event.dataTransfer.setData('text/plain',courses.filter(course=>keys.includes(course.course_key)).map(course=>course.name).join(', '));
                        }
                    }}}
                    value={courses}
                    removableSort
                    scrollable
                    scrollHeight="flex"
                    emptyMessage={<ListEmptyState filtered={listFilters.count > 0} empty={folderId === null ? (allCourses.length ? "No courses here. Create a course or move one here from a folder." : "No courses yet. Create a course to get started.") : "This folder has no courses. Create a course or move one here."} noMatches="No courses match these filters." onClear={listFilters.clear} />}
                >
                    <Column field="name" body={nameBodyTemplate} header="Course" sortable filter={true} filterPlaceholder="Filter course" />
                    <Column field="created_at" header="Created" sortable  filter={true} />
                    <Column field="updated_at" header="Updated" sortable filter={true} />
                    <Column header="" body={(row: ListCourseDto) => (
                        <span className="admin-row-actions">
                            <Button icon="pi pi-pencil" onClick={() => requestAction(() => {
                                setSelectedCourse(row); setSelectedKeys([row.course_key]);
                                startEditCourse(row.course_key);
                                showDetails();
                            })} tooltip='Edit course' aria-label="Edit course" />
                            <Button icon="pi pi-trash" onClick={() => {
                                setSelectedCourse(row);
                                setShowDeleteConfirm(true);
                            }} tooltip='Delete course' aria-label="Delete course" />
                        </span>
                        )}
                    />

                </DataTable>
            </div>
        </div>

        <DeleteEntityConfirmDialog
            visible={showDeleteConfirm}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={async () => {
                deleteCourse(selectedCourse?.course_key ?? '');
                setShowDeleteConfirm(false);
            }}
            entityType='Course'
            entityName={selectedCourse?.name ?? ''}
        />

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />
    </>);
}
